import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Title2,
  Text,
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
} from '@fluentui/react-components';
import { ArrowUploadRegular, ArrowClockwiseRegular } from '@fluentui/react-icons';
import { RoleGuard } from './RoleGuard';
import { UploadModal } from './UploadModal';
import { StatusTrackerTable, UploadRecord, UploadStatus } from './StatusTrackerTable';
import { ChatAPI } from '../utils/api';
import { ProcessingStatusRecord } from '../models/types';
import { useAuth } from '../auth/AuthProvider';

const useStyles = makeStyles({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: tokens.spacingVerticalXXXL,
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    alignItems: 'center',
    textAlign: 'center',
  },
  uploadButton: {
    marginTop: tokens.spacingVerticalM,
  },
  buttonGroup: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
  },
  content: {
    flex: 1,
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXXL,
    gap: tokens.spacingVerticalM,
    textAlign: 'center',
  },
  loadingState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXXL,
    gap: tokens.spacingVerticalM,
  },
});

interface DocumentUploadProps {
  selectedView?: string;
}

/**
 * DocumentUpload page component - handles document uploads and displays processing status.
 */
export const DocumentUpload: React.FC<DocumentUploadProps> = ({ selectedView = 'Authorizations' }) => {
  const classes = useStyles();
  const { isAuthenticated, getToken } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploads, setUploads] = useState<UploadRecord[]>([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Map database status to UI status
  const mapStatus = (record: ProcessingStatusRecord): UploadStatus => {
    // Check statuses in reverse order (most complete first)
    if (record.ready) return 'Ready';
    if (record.ocrCompletionDate) return 'Preprocessing Complete';
    if (record.documentDownloadDate) return 'Details Retrieved';
    if (record.metadataRetrievalDate) return 'Received';
    return 'Received';
  };

  // Fetch processing status from API
  const fetchProcessingStatus = async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      const response = await ChatAPI.getProcessingStatus(100);
      
      const mappedRecords: UploadRecord[] = response.records.map(record => ({
        id: record.complaintId,
        authorizationId: record.complaintId,
        status: mapStatus(record),
        uploadedAt: record.uploadedAt ? new Date(record.uploadedAt) : new Date(),
        errorMessage: record.errorMessage || undefined,
      }));
      
      setUploads(mappedRecords);
      setErrorMessage('');
    } catch (error) {
      console.error('Failed to fetch processing status:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to load processing status');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchProcessingStatus(true);
  };

  /**
   * Load status on mount - but only after authentication is fully ready.
   * 
   * CRITICAL: We must wait for both isAuthenticated AND getToken to be available
   * before making API calls. On page refresh, there's a brief moment where 
   * isAuthenticated is true but the account/token isn't ready yet, causing
   * "No credentials provided" errors.
   * 
   * By depending on both isAuthenticated and getToken, we ensure:
   * 1. User is authenticated (ProtectedRoute already verified this)
   * 2. getToken function is ready and can successfully acquire tokens
   * 3. No race condition with MSAL token acquisition
   */
  useEffect(() => {
    // Only fetch if in Appeals mode
    if (selectedView !== 'Appeals') {
      setIsLoading(false);
      return;
    }

    // Verify token getter is available before fetching
    const verifyAuthAndFetch = async () => {
      if (!isAuthenticated) {
        console.log('[DocumentUpload] Skipping fetch - not authenticated');
        return;
      }
      
      // Test if we can get a token before proceeding
      try {
        const token = await getToken();
        if (!token) {
          console.warn('[DocumentUpload] Token not available yet, will retry...');
          return;
        }
        console.log('[DocumentUpload] Auth ready, fetching processing status');
        fetchProcessingStatus();
      } catch (error) {
        console.error('[DocumentUpload] Token acquisition failed:', error);
        setErrorMessage('Authentication not ready. Please try again.');
        setIsLoading(false);
      }
    };

    verifyAuthAndFetch();
  }, [isAuthenticated, getToken, selectedView]);

  const handleUploadSubmit = async (authorizationId: string) => {
    try {
      // Submit to backend API
      await ChatAPI.processAdhoc(authorizationId);
      
      setIsModalOpen(false);
      setSuccessMessage(`Successfully submitted authorization ${authorizationId} for processing`);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
      
      // Refresh the status list
      fetchProcessingStatus();
    } catch (error) {
      console.error('Failed to submit processing request:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to submit request');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const renderContent = () => {
    if (selectedView === 'Authorizations') {
      return (
        <div className={classes.emptyState}>
          <Text size={500} weight="semibold">
            Authorization Processing requests, coming soon!
          </Text>
        </div>
      );
    }

    // Appeals mode
    if (isLoading) {
      return (
        <div className={classes.loadingState}>
          <Spinner size="large" label="Loading processing status..." />
        </div>
      );
    }

    if (uploads.length === 0) {
      return (
        <div className={classes.emptyState}>
          <Text size={500} weight="semibold">
            No processing requests yet
          </Text>
          <Text>
            Click the "Process Authorization" button above to get started.
          </Text>
        </div>
      );
    }

    return <StatusTrackerTable uploads={uploads} />;
  };

  return (
    <RoleGuard>
      <div className={classes.container}>
        <div className={classes.header}>
          <Title2>Adhoc Processing Center</Title2>
          <Text>
            Submit authorization IDs for processing and track their status through the pipeline.
          </Text>
          
          {selectedView === 'Appeals' && (
            <div className={classes.buttonGroup}>
              <Button
                appearance="primary"
                icon={<ArrowUploadRegular />}
                onClick={() => setIsModalOpen(true)}
                className={classes.uploadButton}
              >
                Process Authorization
              </Button>
              <Button
                appearance="secondary"
                icon={<ArrowClockwiseRegular />}
                onClick={handleRefresh}
                disabled={isRefreshing}
                className={classes.uploadButton}
              >
                {isRefreshing ? 'Refreshing...' : 'Refresh Status'}
              </Button>
            </div>
          )}
        </div>

        <div className={classes.content}>
          {successMessage && (
            <MessageBar intent="success">
              <MessageBarBody>{successMessage}</MessageBarBody>
            </MessageBar>
          )}
          
          {errorMessage && (
            <MessageBar intent="error">
              <MessageBarBody>{errorMessage}</MessageBarBody>
            </MessageBar>
          )}

          {renderContent()}
        </div>
      </div>

      <UploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleUploadSubmit}
      />
    </RoleGuard>
  );
};
